import React, { useEffect, useState, useMemo, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import { useTheme } from "@mui/material/styles";

import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";

import { Box } from "@mui/material";
import DrawerField from "components/Drawer";
import WirelessCongestionLevel from "./components/WirelessCongestionLevel";
import WiredCongestionLevel from "./components/WiredCongestionLevel";
import WiredLinkDegradation from "./components/WiredLinkDegradation";
import WiredCableQuality from "./components/WiredCableQuality";
import WirelessChannelInterface from "./components/WirelessChannelInterface";

const getSeries = (data) => (Array.isArray(data?.series) ? data.series : []);

const getResponseData = (response) => {
  if (!response) return null;
  if (response?.result?.data) return response.result.data;
  if (response?.data) return response.data;
  return null;
};

const getPeakWiredCongestionData = (series = []) => {
  if (!Array.isArray(series) || series.length === 0) {
    return null;
  }

  const values = series.map(([, value]) => Number(value) || 0);

  const maxValue = Math.max(...values);

  // 全部都是 0 不呼叫 peak
  if (maxValue === 0) {
    return null;
  }

  const peakItem = series.find(([, value]) => Number(value) === maxValue);

  return {
    peakhourly: dayjs(peakItem[0]).format("YYYY-MM-DD HH:mm:ss"),
  };
};
const LinkIntegrity = ({ isUnityProSubscribed = true }) => {
  const theme = useTheme();
  const { auth, isAuthReady, standalone } = useAuthScope();
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const [activeDrawer, setActiveDrawer] = useState(null);

  const pendingWirelessCongestionThresholdRef = useRef(null);
  const pendingWiredCongestionThresholdRef = useRef(null);

  const [wirelessCongestionUuid] = useState(uuidv4());
  const [wirelessCongestionDeviceUuid] = useState(uuidv4());

  const [wiredCongestionUuid] = useState(uuidv4());
  const [wiredCongestionPeakUuid] = useState(uuidv4());

  const [wiredLinkDegradationUuid] = useState(uuidv4());
  const [wiredLinkDegradationPortListUuid] = useState(uuidv4());

  const [wiredCableQualityUuid] = useState(uuidv4());
  const [wiredCableQualityPortListUuid] = useState(uuidv4());

  const [wirelessChannelInterfaceUuid, setWirelessChannelInterfaceUuid] =
    useState(uuidv4());

  const [
    wirelessChannelInterfaceApListUuid,
    setWirelessChannelInterfaceApListUuid,
  ] = useState(uuidv4());

  const [radioUuid, setRadioUuid] = useState(uuidv4());

  const [wirelessCongestionApiChartData, setWirelessCongestionApiChartData] =
    useState({});

  const [
    wirelessCongestionDeviceApiChartData,
    setWirelessCongestionDeviceApiChartData,
  ] = useState({});

  const [wiredCongestionApiChartData, setWiredCongestionApiChartData] =
    useState({});

  const [wiredCongestionPeakData, setWiredCongestionPeakData] = useState({});

  const [
    wiredLinkDegradationApiChartData,
    setWiredLinkDegradationApiChartData,
  ] = useState({});

  const [wiredLinkDegradationPortList, setWiredLinkDegradationPortList] =
    useState({});

  const [wiredCableQualityApiChartData, setWiredCableQualityApiChartData] =
    useState({});

  const [wiredCableQualityPortList, setWiredCableQualityPortList] = useState(
    {},
  );

  const [wirelessChannelInterfaceApList, setWirelessChannelInterfaceApList] =
    useState({});

  const [
    wirelessChannelInterfaceApiChartData,
    setWirelessChannelInterfaceApiChartData,
  ] = useState({});

  const [channelInterferenceThreshold, setChannelInterferenceThreshold] =
    useState(0);

  const [radioData, setRadioData] = useState(null);

  const [loadingMap, setLoadingMap] = useState({
    wirelessCongestion: true,
    wirelessCongestionDevice: true,
    wiredCongestion: true,
    wiredCongestionPeak: true,
    wiredLinkDegradation: true,
    wiredLinkDegradationPortList: true,
    wiredCableQuality: true,
    wiredCableQualityPortList: true,
    wirelessChannelInterface: true,
    wirelessChannelInterfaceApList: true,
    radio: true,
  });

  const handleCloseDrawer = () => {
    setActiveDrawer(null);
  };

  const buildApiConfig = (resource) => {
    if (standalone) {
      return {
        path: `/orgs/:orgId/sites/:siteId/${resource}`,
        params: {
          orgId: auth.realm?.id,
          siteId: auth.sites?.id,
        },
      };
    }

    return {
      path: `/ents/:entId/orgs/:orgId/sites/:siteId/${resource}`,
      params: {
        entId: auth.realm?.id,
        orgId: auth.orgs?.id,
        siteId: auth.sites?.id,
      },
    };
  };

  const requestList = useMemo(
    () => [
      {
        uuid: wirelessCongestionUuid,
        resource: "linkIntegrity/wirelessCongestion.read",
        loadingKey: "wirelessCongestion",
      },
      {
        uuid: wirelessCongestionDeviceUuid,
        resource: "linkIntegrity/wirelessCongestion/devices.read",
        loadingKey: "wirelessCongestionDevice",
      },
      {
        uuid: wiredCongestionUuid,
        resource: "linkIntegrity/wiredCongestion.read",
        loadingKey: "wiredCongestion",
      },
      {
        uuid: wiredLinkDegradationUuid,
        resource: "linkIntegrity/wiredLinkDegration.read",
        loadingKey: "wiredLinkDegradation",
      },
      {
        uuid: wiredLinkDegradationPortListUuid,
        resource: "linkIntegrity/wiredLinkDegration/portList.read",
        loadingKey: "wiredLinkDegradationPortList",
      },
      {
        uuid: wiredCableQualityUuid,
        resource: "linkIntegrity/wiredCableQuality.read",
        loadingKey: "wiredCableQuality",
      },
      {
        uuid: wiredCableQualityPortListUuid,
        resource: "linkIntegrity/wiredCableQuality/portList.read",
        loadingKey: "wiredCableQualityPortList",
      },
      {
        uuid: wirelessChannelInterfaceUuid,
        resource: "linkIntegrity/channelInterference.read",
        loadingKey: "wirelessChannelInterface",
      },
      {
        uuid: wirelessChannelInterfaceApListUuid,
        resource: "linkIntegrity/channelInterference/apList.read",
        loadingKey: "wirelessChannelInterfaceApList",
      },
      {
        uuid: radioUuid,
        resource: "settings/radio.read",
        loadingKey: "radio",
      },
    ],
    [
      wirelessCongestionUuid,
      wirelessCongestionDeviceUuid,
      wiredCongestionUuid,
      wiredLinkDegradationUuid,
      wiredLinkDegradationPortListUuid,
      wiredCableQualityUuid,
      wiredCableQualityPortListUuid,
      wirelessChannelInterfaceUuid,
      wirelessChannelInterfaceApListUuid,
      radioUuid,
    ],
  );

  useEffect(() => {
    if (!isUnityProSubscribed) {
      setLoadingMap({
        wirelessCongestion: false,
        wirelessCongestionDevice: false,
        wiredCongestion: false,
        wiredCongestionPeak: false,
        wiredLinkDegradation: false,
        wiredLinkDegradationPortList: false,
        wiredCableQuality: false,
        wiredCableQualityPortList: false,
        wirelessChannelInterface: false,
        wirelessChannelInterfaceApList: false,
        radio: false,
      });
      return;
    }
    if (!isAuthReady || !auth?.sites?.id) return;

    setLoadingMap({
      wirelessCongestion: true,
      wirelessCongestionDevice: true,
      wiredCongestion: true,
      wiredCongestionPeak: true,
      wiredLinkDegradation: true,
      wiredLinkDegradationPortList: true,
      wiredCableQuality: true,
      wiredCableQualityPortList: true,
      wirelessChannelInterface: true,
      wirelessChannelInterfaceApList: true,
      radio: true,
    });

    setWiredCongestionPeakData({});

    let cancelled = false;

    const sendRequestsSequentially = async () => {
      for (const { uuid, resource } of requestList) {
        if (cancelled) return;

        const api = buildApiConfig(resource);
        sendRequest(api.path, uuid, api.params);

        await new Promise((resolve) => setTimeout(resolve, 300));
      }
    };

    sendRequestsSequentially();

    return () => {
      cancelled = true;
    };
  }, [
    isUnityProSubscribed,
    isAuthReady,
    standalone,
    auth?.realm?.id,
    auth?.ents?.id,
    auth?.orgs?.id,
    auth?.sites?.id,
    requestList,
    sendRequest,
  ]);

  useEffect(() => {
    const responseMap = [
      {
        uuid: wirelessCongestionUuid,
        setData: setWirelessCongestionApiChartData,
        loadingKey: "wirelessCongestion",
      },
      {
        uuid: wirelessCongestionDeviceUuid,
        setData: setWirelessCongestionDeviceApiChartData,
        loadingKey: "wirelessCongestionDevice",
      },
      {
        uuid: wiredCongestionUuid,
        setData: setWiredCongestionApiChartData,
        loadingKey: "wiredCongestion",
      },
      {
        uuid: wiredCongestionPeakUuid,
        setData: setWiredCongestionPeakData,
        loadingKey: "wiredCongestionPeak",
      },
      {
        uuid: wiredLinkDegradationUuid,
        setData: setWiredLinkDegradationApiChartData,
        loadingKey: "wiredLinkDegradation",
      },
      {
        uuid: wiredLinkDegradationPortListUuid,
        setData: setWiredLinkDegradationPortList,
        loadingKey: "wiredLinkDegradationPortList",
      },
      {
        uuid: wiredCableQualityUuid,
        setData: setWiredCableQualityApiChartData,
        loadingKey: "wiredCableQuality",
      },
      {
        uuid: wiredCableQualityPortListUuid,
        setData: setWiredCableQualityPortList,
        loadingKey: "wiredCableQualityPortList",
      },
      {
        uuid: wirelessChannelInterfaceUuid,
        setData: setWirelessChannelInterfaceApiChartData,
        loadingKey: "wirelessChannelInterface",
      },
      {
        uuid: wirelessChannelInterfaceApListUuid,
        setData: setWirelessChannelInterfaceApList,
        loadingKey: "wirelessChannelInterfaceApList",
      },
      {
        uuid: radioUuid,
        setData: setRadioData,
        loadingKey: "radio",
      },
    ];

    responseMap.forEach(({ uuid, setData, loadingKey }) => {
      const response = getData(uuid);
      const data = getResponseData(response);

      if (!response) return;
      if (!data) {
        setLoadingMap((prev) => ({
          ...prev,
          [loadingKey]: false,
        }));
        return;
      }

      if (loadingKey === "wirelessCongestion") {
        const pendingThreshold = pendingWirelessCongestionThresholdRef.current;

        const nextData = {
          ...data,
          threshold:
            pendingThreshold !== null ? pendingThreshold : data.threshold,
        };

        setData(nextData);

        if (
          pendingThreshold !== null &&
          Number(data.threshold) === Number(pendingThreshold)
        ) {
          pendingWirelessCongestionThresholdRef.current = null;
        }
      } else if (loadingKey === "wiredCongestion") {
        const pendingThreshold = pendingWiredCongestionThresholdRef.current;

        const nextData = {
          ...data,
          threshold:
            pendingThreshold !== null ? pendingThreshold : data.threshold,
        };

        setData(nextData);

        if (
          pendingThreshold !== null &&
          Number(data.threshold) === Number(pendingThreshold)
        ) {
          pendingWiredCongestionThresholdRef.current = null;
        }
      } else {
        setData(data);
      }

      setLoadingMap((prev) => ({
        ...prev,
        [loadingKey]: false,
      }));
    });
  }, [
    dataMap,
    getData,
    wirelessCongestionUuid,
    wirelessCongestionDeviceUuid,
    wiredCongestionUuid,
    wiredCongestionPeakUuid,
    wiredLinkDegradationUuid,
    wiredLinkDegradationPortListUuid,
    wiredCableQualityUuid,
    wiredCableQualityPortListUuid,
    wirelessChannelInterfaceUuid,
    wirelessChannelInterfaceApListUuid,
    radioUuid
  ]);

  useEffect(() => {
    if (wirelessChannelInterfaceApiChartData?.threshold === undefined) return;

    setChannelInterferenceThreshold(
      Number(wirelessChannelInterfaceApiChartData.threshold),
    );
  }, [wirelessChannelInterfaceApiChartData?.threshold]);

  const wirelessCongestionChartSeriesData = useMemo(() => {
    return getSeries(wirelessCongestionApiChartData);
  }, [wirelessCongestionApiChartData]);

  const wiredCongestionChartSeriesData = useMemo(() => {
    return getSeries(wiredCongestionApiChartData);
  }, [wiredCongestionApiChartData]);

  const wiredLinkDegradationChartSeriesData = useMemo(() => {
    return getSeries(wiredLinkDegradationApiChartData);
  }, [wiredLinkDegradationApiChartData]);

  const wiredCableQualityChartSeriesData = useMemo(() => {
    return getSeries(wiredCableQualityApiChartData);
  }, [wiredCableQualityApiChartData]);

  const wirelessChannelInterfaceChartSeriesData = useMemo(() => {
    return getSeries(wirelessChannelInterfaceApiChartData);
  }, [wirelessChannelInterfaceApiChartData]);

  useEffect(() => {
    if (!isAuthReady || !auth?.sites?.id) return;

    const peakData = getPeakWiredCongestionData(wiredCongestionChartSeriesData);

    if (!peakData) {
      setWiredCongestionPeakData({});

      setLoadingMap((prev) => ({
        ...prev,
        wiredCongestionPeak: false,
      }));

      return;
    }

    setLoadingMap((prev) => ({
      ...prev,
      wiredCongestionPeak: true,
    }));

    const api = buildApiConfig('linkIntegrity/wiredCongestion/peak.read');

    const requestPayload = {
      data: {
        peakhourly: peakData.peakhourly,
      },
    };

    sendRequest(api.path, wiredCongestionPeakUuid, api.params, requestPayload);
  }, [
    isAuthReady,
    standalone,
    auth?.realm?.id,
    auth?.ents?.id,
    auth?.orgs?.id,
    auth?.sites?.id,
    wiredCongestionChartSeriesData,
    wiredCongestionPeakUuid,
    sendRequest,
  ]);
  const analysisData = useMemo(() => {
    const values = wirelessCongestionChartSeriesData.map(([, value]) =>
      Math.round(Number(value) || 0),
    );

    const peakCongested = values.length ? Math.max(...values) : 0;

    const averageCongested = values.length
      ? Math.round(
          values.reduce((sum, value) => sum + value, 0) / values.length,
        )
      : 0;

    const threshold = Number(wirelessCongestionApiChartData?.threshold);

    const congestedApCount =
      Number(wirelessCongestionApiChartData?.congestedApCount) || 0;

    const apCount = Number(wirelessCongestionApiChartData?.apCount) || 0;

    const congestedPercent =
      apCount > 0 ? Math.round((congestedApCount / apCount) * 100) : 0;

    const status =
      congestedPercent >= 20
        ? "Critical"
        : congestedPercent >= 10
          ? "Warn"
          : "Good";
    return {
      totalAps: apCount,
      status,
      peakCongested,
      averageCongested,
      threshold,
      congestedApCount,
      congestedPercent,
      confidence:
        status === "Critical" ? "Low" : status === "Warn" ? "Medium" : "High",
      impact:
        status === "Critical" ? "High" : status === "Warn" ? "Moderate" : "Low",
      impactColor:
        status === "Critical"
          ? theme.palette.primary.redPrimary
          : status === "Warn"
            ? theme.palette.primary.orangeThird
            : theme.palette.primary.greenPrimary,
    };
  }, [
    wirelessCongestionChartSeriesData,
    wirelessCongestionApiChartData,
    theme.palette.primary.redPrimary,
    theme.palette.primary.orangeThird,
    theme.palette.primary.greenPrimary,
  ]);

  const avgRssi = Number(wirelessChannelInterfaceApiChartData?.avgRssi);

  const channelInterferenceStatus =
    avgRssi > -60 ? "Critical" : avgRssi >= -70 ? "Warning" : "Good";

  const statusColorMap = {
    Good: theme.palette.primary.greenPrimary,
    Warning: theme.palette.primary.orangeThird,
    Critical: theme.palette.primary.redPrimary,
  };

  const refreshWirelessCongestion = (nextThreshold) => {
    if (nextThreshold !== undefined && nextThreshold !== null) {
      const normalizedThreshold = Number(nextThreshold);

      pendingWirelessCongestionThresholdRef.current = normalizedThreshold;

      setWirelessCongestionApiChartData((prev) => ({
        ...prev,
        threshold: normalizedThreshold,
      }));
    }

    setLoadingMap((prev) => ({
      ...prev,
      wirelessCongestion: true,
      wirelessCongestionDevice: true,
    }));

    const api = buildApiConfig('linkIntegrity/wirelessCongestion.read');
    sendRequest(api.path, wirelessCongestionUuid, api.params);

    const deviceApi = buildApiConfig('linkIntegrity/wirelessCongestion/devices.read');
    sendRequest(deviceApi.path, wirelessCongestionDeviceUuid, deviceApi.params);
  };

  const refreshWiredCongestion = (nextThreshold) => {
    if (nextThreshold !== undefined && nextThreshold !== null) {
      const normalizedThreshold = Number(nextThreshold);

      pendingWiredCongestionThresholdRef.current = normalizedThreshold;

      setWiredCongestionApiChartData((prev) => ({
        ...prev,
        threshold: normalizedThreshold,
      }));
    }

    setWiredCongestionPeakData({});

    setLoadingMap((prev) => ({
      ...prev,
      wiredCongestion: true,
      wiredCongestionPeak: true,
    }));

    const api = buildApiConfig("linkIntegrity/wiredCongestion.read");
    sendRequest(api.path, wiredCongestionUuid, api.params);
  };

  const refreshWirelessChannelInterface = (nextThreshold) => {
    if (nextThreshold !== undefined && nextThreshold !== null) {
      const normalizedThreshold = Number(nextThreshold);

      setChannelInterferenceThreshold(normalizedThreshold);

      setWirelessChannelInterfaceApiChartData((prev) => ({
        ...prev,
        threshold: normalizedThreshold,
      }));
    }

    setLoadingMap((prev) => ({
      ...prev,
      wirelessChannelInterface: true,
      wirelessChannelInterfaceApList: true,
    }));

    const nextReadUuid = uuidv4();
    const nextApListUuid = uuidv4();

    setWirelessChannelInterfaceUuid(nextReadUuid);
    setWirelessChannelInterfaceApListUuid(nextApListUuid);

    const readApi = buildApiConfig('linkIntegrity/channelInterference.read');
    sendRequest(readApi.path, nextReadUuid, readApi.params);

    const apListApi = buildApiConfig('linkIntegrity/channelInterference/apList.read');
    sendRequest(apListApi.path, nextApListUuid, apListApi.params);
  };

  return (
    <Box>
      <WirelessCongestionLevel
        loading={loadingMap.wirelessCongestion}
        noData={!isUnityProSubscribed}
        onClick={
          Number(analysisData?.totalAps) === 0
            ? undefined
            : () => setActiveDrawer("wirelessCongestionLevel")
        }
        wirelessCongestionChartSeriesData={wirelessCongestionChartSeriesData}
        analysisData={analysisData}
      />

      <WiredCongestionLevel
        loading={loadingMap.wiredCongestion}
        noData={!isUnityProSubscribed}
        onClick={
          Number(wiredCongestionApiChartData?.switchCount) === 0
            ? undefined
            : () => setActiveDrawer("wiredCongestionLevel")
        }
        wiredCongestionChartSeriesData={wiredCongestionChartSeriesData}
        wiredCongestionPeakData={wiredCongestionPeakData}
        wiredCongestionApiChartData={wiredCongestionApiChartData}
      />

      <WiredLinkDegradation
        loading={loadingMap.wiredLinkDegradation}
        noData={!isUnityProSubscribed}
        onClick={
          Number(wiredLinkDegradationApiChartData?.switchCount) === 0
            ? undefined
            : () => setActiveDrawer("wiredLinkDegradation")
        }
        wiredLinkDegradationApiChartData={wiredLinkDegradationApiChartData}
        wiredLinkDegradationChartSeriesData={
          wiredLinkDegradationChartSeriesData
        }
      />

      <WiredCableQuality
        loading={loadingMap.wiredCableQuality}
        noData={!isUnityProSubscribed}
        onClick={
          Number(wiredCableQualityApiChartData?.switchCount) === 0
            ? undefined
            : () => setActiveDrawer("wiredCableQuality")
        }
        wiredCableQualityApiChartData={wiredCableQualityApiChartData}
        wiredCableQualityChartSeriesData={wiredCableQualityChartSeriesData}
      />

      <WirelessChannelInterface
        loading={loadingMap.wirelessChannelInterface || loadingMap.radio}
        noData={!isUnityProSubscribed}
        onClick={
          Number(wirelessChannelInterfaceApiChartData?.noisyAps) === 0
            ? undefined
            : () => setActiveDrawer("wirelessChannelInterface")
        }
        wirelessChannelInterfaceChartSeriesData={
          wirelessChannelInterfaceChartSeriesData
        }
        wirelessChannelInterfaceApiChartData={
          wirelessChannelInterfaceApiChartData
        }
        wirelessChannelInterfaceApList={wirelessChannelInterfaceApList}
        radioData={radioData}
        status={channelInterferenceStatus}
        statusColorMap={statusColorMap}
      />

      {activeDrawer === "wirelessCongestionLevel" && (
        <DrawerField
          open
          handleClose={handleCloseDrawer}
          width="750px"
          contentKey="WirelessCongestionLevel"
          commonContent="ainsightWirelessCongestionLevelDrawer"
          contentProps={{
            loading: loadingMap.wirelessCongestionDevice,
            wirelessCongestionChartSeriesData,
            wirelessCongestionDeviceApiChartData,
            auth,
            analysisData,
            onRefreshWirelessCongestion: refreshWirelessCongestion,
          }}
        />
      )}

      {activeDrawer === "wiredCongestionLevel" && (
        <DrawerField
          open
          handleClose={handleCloseDrawer}
          width="750px"
          contentKey="WiredCongestionLevel"
          commonContent="ainsightWiredCongestionLevelDrawer"
          contentProps={{
            loading: loadingMap.wiredCongestionPeak,
            wiredCongestionChartSeriesData,
            wiredCongestionPeakData,
            wiredCongestionApiChartData,
            auth,
            onRefreshWiredCongestion: refreshWiredCongestion,
          }}
        />
      )}

      {activeDrawer === "wiredLinkDegradation" && (
        <DrawerField
          open
          handleClose={handleCloseDrawer}
          width="750px"
          contentKey="WiredLinkDegradation"
          commonContent="ainsightWiredLinkDegradationDrawer"
          contentProps={{
            loading: loadingMap.wiredLinkDegradationPortList,
            wiredLinkDegradationApiChartData,
            wiredLinkDegradationChartSeriesData,
            wiredLinkDegradationPortList,
            auth,
          }}
        />
      )}

      {activeDrawer === "wiredCableQuality" && (
        <DrawerField
          open
          handleClose={handleCloseDrawer}
          width="750px"
          contentKey="WiredCableQuality"
          commonContent="ainsightWiredCableQualityDrawer"
          contentProps={{
            loading: loadingMap.wiredCableQualityPortList,
            wiredCableQualityChartSeriesData,
            wiredCableQualityApiChartData,
            wiredCableQualityPortList,
            auth,
          }}
        />
      )}

      {activeDrawer === "wirelessChannelInterface" && (
        <DrawerField
          open
          handleClose={handleCloseDrawer}
          width="750px"
          contentKey="WirelessChannelInterface"
          commonContent="ainsightWirelessChannelInterfaceDrawer"
          contentProps={{
            loading: loadingMap.wirelessChannelInterfaceApList,
            wirelessChannelInterfaceChartSeriesData,
            wirelessChannelInterfaceApiChartData,
            wirelessChannelInterfaceApList,
            auth,
            onRefreshWirelessChannelInterface: refreshWirelessChannelInterface,
          }}
        />
      )}
    </Box>
  );
};

export default LinkIntegrity;
